/* eslint-disable max-len */
/* eslint-disable no-console */
import { toast } from 'react-toastify';
import { newNewsFeedUploadFiles } from '../../../../../../../../../libs/HelperContent';

// Podcast URL data interface
export interface PodcastUrlData {
	url: string;
	title?: string;
	description?: string;
	fileType: string;
	platform?: string;
	metadata?: {
		isDataUri?: boolean;
		sizeInBytes?: number;
		duration?: number;
		fileSize?: number;
	};
}

/**
 * Get audio file duration
 * @param file - Audio file
 * @returns Promise with duration in seconds
 */
export const getAudioDuration = (file: File): Promise<number> =>
{
	return new Promise((resolve, reject) =>
	{
		const audio = new Audio();

		audio.onloadedmetadata = () =>
		{
			resolve(audio.duration);
		};
		audio.onerror = () =>
		{
			reject(new Error('Failed to load audio file'));
		};
		audio.src = URL.createObjectURL(file);
	});
};

/**
 * Enhanced audio upload handler that supports both file uploads and URL uploads
 * @param fileOrUrl - Audio file or URL string
 * @param companyId - Company ID
 * @param metadata - Additional audio metadata
 * @param isUrl - Boolean flag to indicate if input is URL (optional, auto-detected)
 * @returns Promise with upload result
 */
export const handleAudioUpload = async (
	fileOrUrl: File | string,
	companyId: string,
	metadata: {
		title?: string;
		description?: string;
		episodeNumber?: number;
	} = {},
	isUrl?: boolean
) =>
{
	const isUrlInput = isUrl !== undefined ? isUrl : typeof fileOrUrl === 'string';

	try
	{
		let audioPayload: any;

		if (isUrlInput)
		{
			// Handle URL upload - extract metadata and build payload
			const audioUrl = fileOrUrl as string;
			const filename = audioUrl.split('/').pop() || 'audio.mp3';
			const fileType = filename.split('.').pop()
				?.toLowerCase() || 'mp3';

			// Estimate file size
			let estimatedSize = 5 * 1024 * 1024; // 5MB default

			try
			{
				const response = await fetch(audioUrl, { method: 'HEAD' });
				const contentLength = response.headers.get('content-length');

				if (contentLength) estimatedSize = parseInt(contentLength, 10);
			}
			catch (error) { /* Use default size */ }

			audioPayload = {
				title       : metadata.title || filename.split('.')[0] || 'Untitled Episode',
				description : metadata.description || '',
				audioUrl,
				fileType    : [ 'mp3', 'wav', 'm4a', 'ogg' ].includes(fileType) ? fileType as 'mp3' | 'wav' | 'm4a' | 'ogg' : 'mp3',
				fileSize    : estimatedSize,
				duration    : 0,
				...(metadata.episodeNumber && { episodeNumber: metadata.episodeNumber })
			};
		}
		else
		{
			// Handle File upload (existing functionality)
			const file = fileOrUrl as File;

			// Step 1: Upload audio file to S3
			const uploadResult = await newNewsFeedUploadFiles([ file ], `${companyId}/resource`);
			const { name, url, type, size, key } = uploadResult[0];

			// Ensure fileType is one of the accepted types for the API
			let fileType = type.split('/')[1] || 'mp3';

			if (![ 'mp3', 'wav', 'm4a', 'ogg' ].includes(fileType))
			{
				fileType = 'mp3'; // Default to mp3 if not recognized
			}

			// Step 2: Try to get audio duration
			let duration = 0;

			try
			{
				duration = await getAudioDuration(file);
			}
			catch (error)
			{
				console.warn('Could not determine audio duration:', error);
			}

			// Step 3: Build audio payload with metadata
			audioPayload = {
				title       : metadata.title || name || file.name.replace(/\.[^/.]+$/, ''),
				description : metadata.description || '',
				audioUrl    : url,
				fileType    : fileType as 'mp3' | 'wav' | 'm4a' | 'ogg',
				fileSize    : size,
				key,
				duration    : Math.round(duration || 0),
				...(metadata.episodeNumber && { episodeNumber: metadata.episodeNumber })
			};
		}

		return audioPayload;
	}
	catch (error)
	{
		console.error('Error in audio upload process:', error);
		toast.error('Failed to upload audio. Please try again.');
		throw error;
	}
};